/**
 * Notification Service
 * Handles browser notifications, sounds, and notification management
 */

class NotificationService {
  constructor() {
    this.permission = null;
    this.audioContext = null;
    this.soundEnabled = true;
    this.activeSoundLoops = new Map(); // Track active looping sounds
    this.audioContextInitialized = false;
    this.init();
  }

  async init() {
    // Request notification permission
    if ('Notification' in window) {
      this.permission = Notification.permission;
      if (this.permission === 'default') {
        // Don't request automatically - let user request it
        console.log('📢 Notification permission not yet requested');
      }
    }

    // Initialize audio context for sounds
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.warn('AudioContext not supported:', e);
    }
  }

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.permission = 'granted';
      return true;
    }

    if (Notification.permission === 'denied') {
      console.warn('Notification permission was denied');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Play notification sound
   */
  playSound(frequency = 800, duration = 200, type = 'beep') {
    if (!this.soundEnabled || !this.audioContext) {
      return;
    }

    try {
      if (type === 'beep') {
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration / 1000);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + duration / 1000);
      } else if (type === 'alert') {
        // Play a more urgent alert sound
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.frequency.value = 1000;
        oscillator.type = 'square';

        gainNode.gain.setValueAtTime(0.5, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + 0.3);
      }
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  }

  /**
   * Initialize audio context (required for mobile browsers)
   * Must be called after user interaction
   */
  async initializeAudioContext() {
    if (this.audioContextInitialized && this.audioContext) {
      return this.audioContext;
    }

    try {
      // Resume audio context if it's suspended (common on mobile)
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
        this.audioContextInitialized = true;
        return this.audioContext;
      }

      // Create new audio context if it doesn't exist
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        this.audioContextInitialized = true;
      }

      return this.audioContext;
    } catch (error) {
      console.warn('Could not initialize audio context:', error);
      return null;
    }
  }

  /**
   * Play looping sound that repeats until stopped
   * @param {string} id - Unique identifier for this sound loop
   * @param {number} interval - Interval between sounds in milliseconds (default: 2000ms = 2 seconds)
   * @param {string} type - Sound type: 'beep' or 'alert'
   */
  async playLoopingSound(id, interval = 2000, type = 'alert') {
    if (!this.soundEnabled) {
      return;
    }

    // Try to initialize audio context (important for mobile)
    await this.initializeAudioContext();

    if (!this.audioContext) {
      console.warn('Audio context not available, sound will not play');
      return;
    }

    // Stop any existing loop with the same id
    this.stopLoopingSound(id);

    // Play immediately
    this.playSound(1000, 300, type);

    // Set up interval to repeat
    const loopInterval = setInterval(() => {
      if (this.soundEnabled && this.audioContext) {
        this.playSound(1000, 300, type);
      }
    }, interval);

    // Store the interval so we can stop it later
    this.activeSoundLoops.set(id, loopInterval);
    console.log(`🔊 Started looping sound for: ${id}`);
  }

  /**
   * Stop a looping sound
   * @param {string} id - Unique identifier for the sound loop to stop
   */
  stopLoopingSound(id) {
    const loopInterval = this.activeSoundLoops.get(id);
    if (loopInterval) {
      clearInterval(loopInterval);
      this.activeSoundLoops.delete(id);
      console.log(`🔇 Stopped looping sound for: ${id}`);
    }
  }

  /**
   * Stop all looping sounds
   */
  stopAllLoopingSounds() {
    this.activeSoundLoops.forEach((interval, id) => {
      clearInterval(interval);
      console.log(`🔇 Stopped looping sound for: ${id}`);
    });
    this.activeSoundLoops.clear();
  }

  /**
   * Show browser notification
   */
  async showNotification(title, options = {}) {
    const {
      body = '',
      icon = '/logo192.png',
      badge = '/logo192.png',
      tag = 'default',
      requireInteraction = false,
      silent = false,
      sound = true,
      soundType = 'beep',
      onClick = null,
      data = null
    } = options;

    // Play sound if enabled
    if (sound && this.soundEnabled) {
      this.playSound(800, 200, soundType);
    }

    // Check permission
    if (this.permission !== 'granted') {
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        console.warn('Cannot show notification: permission denied');
        return null;
      }
    }

    try {
      const notificationOptions = {
        body,
        icon,
        badge,
        tag,
        requireInteraction,
        silent,
        data: data || {}
      };

      const notification = new Notification(title, notificationOptions);

      // Handle click
      if (onClick) {
        notification.onclick = (event) => {
          event.preventDefault();
          onClick(notification, event);
          notification.close();
        };
      } else {
        // Default click behavior - focus window
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }

      // Auto close after 5 seconds if not requireInteraction
      if (!requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  /**
   * Show parking reminder notification with looping sound
   */
  async showParkingReminder(clientName, jobId, onClick = null) {
    const soundId = `parking-${jobId}`;
    
    // Start looping sound immediately
    this.playLoopingSound(soundId, 2000, 'alert'); // Repeat every 2 seconds
    
    // Create custom onClick handler that stops the sound
    const handleClick = (notification, event) => {
      // Stop the looping sound when notification is clicked
      this.stopLoopingSound(soundId);
      
      if (onClick) {
        onClick(notification, event);
      } else {
        window.focus();
      }
      notification.close();
    };

    const notification = await this.showNotification('🅿️ Podsetnik za parking', {
      body: `Da li ste uplatili parking za posao kod ${clientName}?`,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: `parking-${jobId}`,
      requireInteraction: true,
      sound: false, // Don't play single sound, we're using loop
      soundType: 'alert',
      onClick: handleClick,
      data: {
        type: 'parking',
        jobId,
        clientName,
        soundId // Store soundId in data so we can stop it later
      }
    });

    // Also stop sound if notification is closed by other means
    if (notification) {
      const originalClose = notification.close.bind(notification);
      notification.close = () => {
        this.stopLoopingSound(soundId);
        originalClose();
      };
    }

    return notification;
  }

  /**
   * Stop parking reminder sound (called when user confirms parking is extended)
   */
  stopParkingReminderSound(jobId) {
    const soundId = `parking-${jobId}`;
    this.stopLoopingSound(soundId);
  }

  /**
   * Enable/disable sound
   */
  setSoundEnabled(enabled) {
    this.soundEnabled = enabled;
    localStorage.setItem('notificationSoundEnabled', enabled.toString());
  }

  /**
   * Check if sound is enabled
   */
  isSoundEnabled() {
    const stored = localStorage.getItem('notificationSoundEnabled');
    if (stored !== null) {
      this.soundEnabled = stored === 'true';
    }
    return this.soundEnabled;
  }

  /**
   * Check if notifications are supported
   */
  isSupported() {
    return 'Notification' in window;
  }

  /**
   * Check current permission status
   */
  getPermissionStatus() {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }
}

// Create singleton instance
const notificationService = new NotificationService();

// Load sound preference from localStorage
notificationService.isSoundEnabled();

export default notificationService;

